/**
 * Tipos internos da tela de Mailing.
 *
 * Papel: aqui ficam `ContatoNoFormulario`, o formato que o react-hook-form
 * guarda enquanto a pessoa preenche, e os dois valores em branco de onde a
 * criação parte.
 *
 * `ContatoNoFormulario` é derivado de `novoContatoMailingSchema` com `z.input`,
 * e não escrito à mão, porque é a entrada daquele schema que o formulário
 * precisa produzir. Um tipo próprio compilaria e passaria a divergir no dia em
 * que um campo mudasse de forma no contrato — e a divergência apareceria como
 * erro de gravação, não de compilação.
 *
 * O que o formulário guarda não é o que a API recebe: aqui a ausência é string
 * vazia, porque um campo esvaziado devolve `''` e não `null`. A conversão para
 * `null` é do schema, no envio, e continua valendo para quem chamar a API sem
 * passar por esta tela.
 */
import { type TipoTelefone, type novoContatoMailingSchema } from '@concierge/shared';
import { type z } from 'zod';

/** O contato como o formulário o guarda, antes de o schema normalizar. */
export type ContatoNoFormulario = z.input<typeof novoContatoMailingSchema>;

/** Uma linha de telefone no formulário. */
export interface TelefoneNoFormulario {
  tipo: TipoTelefone;
  /** O número como está no campo, com a máscara aplicada. */
  numero: string;
  falarCom: string;
}

/** Uma linha de telefone recém-acrescentada, antes de qualquer digitação. */
export const TELEFONE_EM_BRANCO: TelefoneNoFormulario = {
  tipo: 'celular',
  numero: '',
  falarCom: '',
};

/**
 * Um contato em branco, como o formulário de criação começa.
 *
 * Todo campo opcional entra como string vazia, e não ausente: com `undefined`,
 * o `register` montaria um campo sem valor inicial, e o React passaria a
 * reclamar da troca de não controlado para controlado na primeira tecla.
 */
export const CONTATO_EM_BRANCO: ContatoNoFormulario = {
  nome: '',
  categoria: '',
  email: '',
  endereco: '',
  observacao: '',
  telefones: [TELEFONE_EM_BRANCO],
};
